import React, { useState, useEffect, useRef } from "react";
import styles from "./Navbar.module.css";
import {
  FaWhatsapp, FaCalendarCheck, FaBullhorn,
  FaUserFriends, FaChevronDown, FaTimes,
  FaFolderOpen, FaFileSignature,
  FaUsers, FaBoxOpen,
} from "react-icons/fa";
import { TEMP_CRM_PIVOT } from "../../config/tempCrmPivot";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Logo from "../../assets/custom-theme-logo.png";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { trackWhatsAppClick } from "../../utils/analytics";
import { Link, useLocation } from "react-router-dom";
import { getOnboardingUrl, PORTAL_LOGIN } from "../../utils/portalUrl";

const FEATURED_LINKS = TEMP_CRM_PIVOT
  ? [
      { key: 'crm',           path: '/features/crm',           Icon: FaUsers       },
      { key: 'notifications', path: '/features/notifications', Icon: FaBullhorn    },
      { key: 'products',      path: '/features/products',      Icon: FaBoxOpen     },
      { key: 'records',       path: '/features/records',       Icon: FaFolderOpen  },
      { key: 'team',          path: '/features/team',          Icon: FaUserFriends },
    ]
  : [
      { key: 'booking',       path: '/features/online-booking',  Icon: FaCalendarCheck },
      { key: 'agreements',    path: '/features/agreements',      Icon: FaFileSignature },
      { key: 'notifications', path: '/features/notifications',   Icon: FaBullhorn      },
      { key: 'team',          path: '/features/team',            Icon: FaUserFriends   },
    ];

// First 6 shown in dropdown; full list accessible via /for
const BIZ_LINKS = [
  { key: 'barber',  path: '/for/barber-shops' },
  { key: 'hair',    path: '/for/hair-salons'  },
  { key: 'nails',   path: '/for/nail-salons'  },
  { key: 'dental',  path: '/for/dental'       },
  { key: 'massage', path: '/for/massage'      },
  { key: 'spa',     path: '/for/spa'          },
  { key: 'medspa',  path: '/for/med-spa'      },
  { key: 'fitness', path: '/for/fitness'      },
  { key: 'tanning', path: '/for/tanning'      },
  { key: 'tattoo',  path: '/for/tattoo'       },
  { key: 'pet',     path: '/for/pet-grooming' },
  { key: 'beauty',  path: '/for/beauty'       },
  { key: 'clinics', path: '/for/clinics'      },
  { key: 'online',  path: '/for/online'       },
  { key: 'laser',     path: '/for/laser-hair-removal' },
  { key: 'aesthetic', path: '/for/aesthetic-clinics'  },
  { key: 'academies', path: '/for/academies'          },
  { key: 'rentals',   path: '/for/vacation-rentals'   },
  { key: 'physio',    path: '/for/physiotherapy'      },
  { key: 'nutrition', path: '/for/nutrition'          },
  { key: 'other',   path: '/for/other'        },
];

const BIZ_DROPDOWN_COUNT = 6;
const WHATSAPP_URL = "https://wa.me/972533920842";

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.language === 'he';
  const [openMenu, setOpenMenu] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const [bizOpen, setBizOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const location = useLocation();
  const featuresRef = useRef(null);
  const bizRef = useRef(null);
  const featuresCloseTimer = useRef(null);
  const bizCloseTimer = useRef(null);

  const ChevronIcon = isRtl ? FiChevronLeft : FiChevronRight;

  const openFeatures = () => { clearTimeout(featuresCloseTimer.current); setFeaturesOpen(true); };
  const closeFeatures = () => { featuresCloseTimer.current = setTimeout(() => setFeaturesOpen(false), 200); };
  const openBiz = () => { clearTimeout(bizCloseTimer.current); setBizOpen(true); };
  const closeBiz = () => { bizCloseTimer.current = setTimeout(() => setBizOpen(false), 200); };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (featuresRef.current && !featuresRef.current.contains(e.target)) setFeaturesOpen(false);
      if (bizRef.current && !bizRef.current.contains(e.target)) setBizOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    document.body.style.overflow = openMenu ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [openMenu]);

  return (
    <div className={styles.position_sticky}>
      {(featuresOpen || bizOpen) && <div className={styles.dropdownBackdrop} />}

      {/* ── Navbar bar ── */}
      <div className={`${styles.navbar_container} ${scrolled ? styles.glassy : ''}`}>
        {/* Logo */}
        <section className={styles.logo}>
          <Link to="/#home"><img src={Logo} alt="Stylon" /></Link>
        </section>

        {/* Desktop center nav links */}
        <section className={styles.links}>
          <ul>
            {/* Home */}
            <Link to="/" className={styles.navRouterLink}>
              <li style={{ borderBottom: location.pathname === '/' ? '2px solid #203052' : undefined }}>
                {t('nav.home')}
              </li>
            </Link>

            {/* Features dropdown */}
            <li className={styles.dropdownItem} ref={featuresRef}
              onMouseEnter={openFeatures} onMouseLeave={closeFeatures}>
              <Link to="/features" className={styles.navRouterLink}
                style={{ display: 'flex', alignItems: 'center', gap: 4,
                  borderBottom: location.pathname.startsWith('/features') ? '2px solid #203052' : undefined }}>
                {t('nav.features')} <FaChevronDown className={styles.chevron} />
              </Link>
              {featuresOpen && (
                <div className={styles.dropdown} onMouseEnter={openFeatures} onMouseLeave={closeFeatures}>
                  <div className={styles.dropdownList}>
                    {FEATURED_LINKS.map(({ key, path, Icon }) => (
                      <Link key={key} to={path} className={styles.dropdownLink} onClick={() => setFeaturesOpen(false)}>
                        <Icon className={styles.dropdownIcon} />
                        <span>{t(`features.cards.${key}.title`)}</span>
                      </Link>
                    ))}
                    <Link to="/features" className={styles.dropdownViewAll} onClick={() => setFeaturesOpen(false)}>
                      {t('nav.viewAllFeatures')} →
                    </Link>
                  </div>
                </div>
              )}
            </li>

            {!TEMP_CRM_PIVOT && (
            <li className={styles.dropdownItem} ref={bizRef}
              onMouseEnter={openBiz} onMouseLeave={closeBiz}>
              <button
                type="button"
                className={styles.navDropdownTrigger}
                onClick={() => setBizOpen(!bizOpen)}
                style={{ borderBottom: location.pathname.startsWith('/for') ? '2px solid #203052' : undefined }}
              >
                {t('nav.bizTypes')} <FaChevronDown className={`${styles.chevron} ${bizOpen ? styles.chevronOpen : ''}`} />
              </button>
              {bizOpen && (
                <div className={styles.dropdown} onMouseEnter={openBiz} onMouseLeave={closeBiz}>
                  <div className={styles.dropdownList}>
                    {BIZ_LINKS.slice(0, BIZ_DROPDOWN_COUNT).map(({ key, path }) => (
                      <Link key={key} to={path} className={styles.dropdownLink} onClick={() => setBizOpen(false)}>
                        <span>{t(`businesses.cards.${key}.title`)}</span>
                      </Link>
                    ))}
                    <Link to="/for" className={styles.dropdownViewAll} onClick={() => setBizOpen(false)}>
                      {t('nav.showMore')} →
                    </Link>
                  </div>
                </div>
              )}
            </li>
            )}

            <Link to="/pricing" className={styles.navRouterLink}>
              <li style={{ borderBottom: location.pathname === '/pricing' ? '2px solid #203052' : undefined }}>
                {t('nav.pricing')}
              </li>
            </Link>

            <Link to="/setup-app" className={styles.navRouterLink}>
              <li style={{ borderBottom: location.pathname === '/setup-app' ? '2px solid #203052' : undefined }}>
                {t('nav.app')}
              </li>
            </Link>
          </ul>
        </section>

        {/* Right side: Start Now + Hamburger (always visible) */}
        <section className={styles.right_actions}>
          <a href={getOnboardingUrl("navbar")}
            className={styles.tryNowLink}>
            <button type="button" className={styles.btnPrimary}>
              {t("nav.startNow")}
            </button>
          </a>
          <button className={styles.hamburgerBtn} onClick={() => setOpenMenu(true)} aria-label="Open menu">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" d="M4 8a1 1 0 0 1 1-1h22a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1m0 8a1 1 0 0 1 1-1h22a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1m0 8a1 1 0 0 1 1-1h22a1 1 0 1 1 0 2H5a1 1 0 0 1-1-1" clipRule="evenodd" />
            </svg>
          </button>
        </section>
      </div>

      {/* ── Drawer ── */}
      {openMenu && <div className={styles.drawerOverlay} onClick={() => setOpenMenu(false)} />}
      <div className={`${styles.drawer} ${openMenu ? styles.drawerOpen : ""}`}>

        <div className={styles.drawerHeader}>
          <button className={styles.drawerClose} onClick={() => setOpenMenu(false)} aria-label="Close menu">
            <FaTimes />
          </button>
        </div>

        <nav className={styles.drawerNav}>

          {/* Mobile-only nav links */}
          <div className={`${styles.drawerCard} ${styles.drawerCardMobileOnly}`}>
            <Link to="/" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.home")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            {!TEMP_CRM_PIVOT && (
            <Link to="/for" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.bizTypes")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            )}
            <Link to="/features" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.features")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            <Link to="/pricing" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.pricing")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
          </div>

          {/* WhatsApp + Language */}
          <div className={styles.drawerCard}>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
              className={styles.drawerCardLink} onClick={() => { trackWhatsAppClick("drawer"); setOpenMenu(false); }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaWhatsapp style={{ color: '#25d366', fontSize: 17 }} />
                WhatsApp
              </span>
              <ChevronIcon className={styles.drawerChevron} />
            </a>
            <div className={styles.drawerCardLang}>
              <LanguageSwitcher variant="drawer" />
            </div>
          </div>

          {/* About, Guides, Support + Login */}
          <div className={styles.drawerCard}>
            <Link to="/about" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.aboutUs")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            <Link to="/guides" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.guides")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            <Link to="/setup-app" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.app")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            <Link to="/support" className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.support")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </Link>
            <a href={PORTAL_LOGIN} target="_blank" rel="noopener noreferrer"
              className={styles.drawerCardLink} onClick={() => setOpenMenu(false)}>
              <span>{t("nav.login")}</span>
              <ChevronIcon className={styles.drawerChevron} />
            </a>
          </div>

        </nav>
      </div>
    </div>
  );
};

export default Navbar;
